import {
  autoUpdate,
  flip,
  offset as floatingOffset,
  Placement,
  shift,
  size,
  useFloating,
  UseFloatingOptions,
  useTransitionStyles,
} from '@floating-ui/react';
import { useState } from 'react';

export interface FloatingPanelProps {
  stateOpened: [boolean, (v: boolean) => void];
  matchWidth?: boolean;
  offset?: number;
  /**
   * Utilize useState para essas refs!
   */
  elements?: UseFloatingOptions['elements'];
  placement?: Placement;
  strategy?: UseFloatingOptions['strategy'];
  fallbackPlacements?: Placement[];
  heightAvaliableCallback?: (height: number) => void;
}

export default function useFloatingPanel({
  stateOpened,
  matchWidth = false,
  offset = 0,
  placement = 'bottom',
  strategy = 'fixed',
  fallbackPlacements = ['top', 'bottom', 'right-start', 'right'],
  heightAvaliableCallback,
  elements,
}: FloatingPanelProps) {
  const [opened, setOpened] = stateOpened;
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  const { refs, floatingStyles, context } = useFloating({
    elements,
    open: opened,
    onOpenChange: setOpened,
    whileElementsMounted: autoUpdate,
    transform: false,
    placement,
    strategy,
    middleware: [
      floatingOffset(offset),
      flip({
        fallbackPlacements,
      }),
      shift(),
      size({
        apply({ rects, elements, availableHeight }: any) {
          if (matchWidth) {
            Object.assign(elements.floating.style, {
              width: `${rects.reference.width}px`,
            });
          }
          setMaxHeight(availableHeight);
          heightAvaliableCallback?.(availableHeight);
        },
      }),
    ],
  });

  const { isMounted, styles } = useTransitionStyles(context, {
    duration: 250,
    initial: ({ side }) => ({
      transform: ['top', 'bottom'].includes(side) ? 'translateY(10px)' : '',
      opacity: 0,
    }),
    common: {
      transform: 'translateY(0px)',
      opacity: 1,
    },
  });

  return {
    panelRef: refs.setFloating, // as unknown as RefObject<HTMLElement>,
    panelStyles: {
      ...floatingStyles,
      maxHeight,
      ...styles,
    } as React.CSSProperties,
    anchorRef: refs.setReference, // as unknown as RefObject<HTMLElement>,
    isMounted,
  };
}
